/**
 * The audit log's fixed vocabulary — filter chips, type pills, and copy.
 *
 * Which chips exist and which verbs each admits is decided here, once;
 * `activity.model.ts` only walks the table, and a new activity type that is
 * missing from `ACTIVITY_TAGS` fails to compile rather than reaching a row
 * untagged. Nothing here reads a feed.
 *
 * Three chips are disabled by design and several tags refuse the obvious
 * word. Each refusal is argued beside the entry rather than here, because the
 * reason is specific to that verb — the running theme is that a tag may name
 * a fact the registry holds and never a claim somebody made.
 */

import type { AccessSubject, ActivityType } from "@ondo/shared";

import type { TagTone } from "../lib/Tag.tsx";

/**
 * The two filter axes, and the sentinel for "everything".
 *
 * A constant rather than the literal string, so it cannot collide with a real
 * ONDO id on the user axis.
 */
export const ALL = "all";

/** The label above each `ChipRow`, and its accessible name. */
export const FILTER_LABELS = {
  user: "User",
  action: "Action type",
} as const;

/**
 * The action-type chips, in the reference design's order.
 *
 * THREE OF THESE ARE DISABLED, AND ON PURPOSE. The design was drawn against a
 * product that records more than this one does, and the honest thing is to show
 * what is missing rather than quietly drop the control — the same choice
 * `capabilities.transferWork` makes, where the server says no and the client
 * renders the refusal instead of hiding the button.
 *
 * `verbs` is the set a chip admits. `title` is the disabled explanation, shown
 * as a tooltip and read out as the accessible description.
 */
export const ACTION_FILTERS: readonly {
  id: string;
  label: string;
  glyph: string;
  verbs: readonly ActivityType[];
  /** Null when the chip works. A sentence when there is nothing to show. */
  absent: string | null;
  /** Sale and auction only — the two kinds that move title. See `bucketOf`. */
  titleMovingOnly?: true;
}[] = [
  { id: ALL, label: "All", glyph: "", verbs: [], absent: null },
  {
    id: "registered",
    label: "Registered",
    glyph: "✦",
    verbs: ["work.registered", "collection.created"],
    absent: null,
  },
  {
    id: "transfer",
    label: "Transfer",
    glyph: "◉",
    // The three sale verbs join `provenance.added` here, and the pairing is the
    // point of this filter rather than a widening of it: a `sale` entry on a
    // chain of title is a CLAIM about ownership and `work.sold` is a FACT, and
    // somebody asking "what has changed hands" wants both, told apart by their
    // tags rather than by two filters.
    verbs: [
      "provenance.added",
      "work.sold",
      "work.title_transferred",
      "work.sale_ended",
    ],
    absent: null,
    titleMovingOnly: true,
  },
  {
    id: "permission",
    label: "Permission",
    glyph: "⊙",
    verbs: [],
    absent:
      "Permissions and entity changes are not recorded as activity yet, so this filter has nothing to show.",
  },
  {
    id: "granted",
    label: "Granted",
    glyph: "⇢",
    verbs: ["collection.shared"],
    absent: null,
  },
  {
    id: "viewed",
    label: "Viewed",
    glyph: "◌",
    verbs: [],
    // STILL DISABLED, AND NO LONGER FOR THE OLD REASON — OND-68. Reads are
    // recorded now; they are simply not ACTIVITY. There is no view verb in
    // `ACTIVITY_TYPES` and there must not be one: an activity is a change to a
    // record, lives in Stream, and may be silently lost. A view lives in
    // `ondo.access_events`, which is append-only and written inside the request
    // that served it. Giving this chip verbs would mean putting the audit into
    // the feed, which is the one place it cannot be kept.
    absent:
      "Reads are on the Access trail, not here. This list is what was done to a record; a view is not one of those, and the two are kept apart because only one of them can be lost.",
  },
  {
    id: "verification",
    label: "Verification",
    glyph: "✓",
    verbs: [
      "provenance.verification_requested",
      "provenance.verified",
      "provenance.disputed",
      "provenance.verification_declined",
      "provenance.verification_referred",
    ],
    absent: null,
  },
  {
    /**
     * OND-75. The label was "KYC" and the id still is, because the id is a key
     * nothing prints.
     *
     * The axis is disabled and its own caption says identity verification
     * happens elsewhere, so this was never the mislabel the ticket is about —
     * that one sat over `ondo.principals.verified` on the gallery's agents
     * screen. But the ticket asks that ONDO never print the phrase "on any
     * screen or in any activity line", and a "KYC" chip on the audit screen
     * reads as a thing the registry records whatever the caption underneath it
     * says. "Identity" names the axis without claiming the apparatus.
     */
    id: "kyc",
    label: "Identity",
    glyph: "◍",
    verbs: [],
    absent: "Identity verification happens outside the registry and files no activity.",
  },
];

/**
 * The pill in the TYPE column.
 *
 * A tone names the KIND of fact, never a colour — see `Tag`. A total record, so
 * a tenth activity type is a compile error here rather than an untagged row.
 */
export const ACTIVITY_TAGS: Record<ActivityType, { label: string; tone: TagTone }> = {
  "collection.created": { label: "Collection", tone: "quiet" },
  "collection.shared": { label: "Granted", tone: "quiet" },
  "work.registered": { label: "Registered", tone: "neutral" },
  // Neither `verified` nor `contested`: representation is a fact about who acts
  // for the holder, and the registry draws no conclusion from it.
  "work.agent_assigned": { label: "Agent", tone: "neutral" },
  "work.agent_removed": { label: "Agent", tone: "quiet" },
  // "Requested" and "Answer", the pair the verification verbs already use, and
  // not "Agent": an ask is not a mandate, and a row tagged the same as an
  // assignment would read as one on a trail somebody scans rather than reads.
  "work.representation_requested": { label: "Requested", tone: "pending" },
  "work.representation_answered": { label: "Answer", tone: "answer" },
  // "Consigned" on all three, and NOT "Sold" on the ending, however it ended.
  // A consignment closing with an outcome of `sold` is the gallery saying it
  // sold — a claim, like a `sale` on a chain of title — and a trail that tagged
  // it SOLD would be reporting an act the registry cannot yet perform.
  "consignment.offered": { label: "Consigned", tone: "pending" },
  "consignment.accepted": { label: "Consigned", tone: "neutral" },
  "consignment.ended": { label: "Consigned", tone: "quiet" },
  // The acceptance's tag exactly, because the arrangement is in the same state:
  // live, on terms both parties agreed. The tag is the reader's answer to "what
  // is this row about", and a repricing is about a consignment.
  "consignment.terms_revised": { label: "Consigned", tone: "neutral" },
  // "Loaned" on the three that are possession, and "Loan" on the two that are
  // a conversation. A proposal is not possession — `feedsFor` keeps it off the
  // work's feed entirely, because "asserting unilaterally that Tate has your
  // painting is a claim about Tate" — so a pill reading LOANED on a row that
  // only asked would tag a claim as a fact, which is the refusal running
  // through this whole map.
  //
  // Never "Sold" and never "Ownership": a loan moves possession and says
  // nothing at all about title, which is the distinction `ondo.loans` was built
  // on and the reason it is not a transfer.
  "loan.proposed": { label: "Loan", tone: "pending" },
  "loan.withdrawn": { label: "Loan", tone: "quiet" },
  "loan.agreed": { label: "Loaned", tone: "neutral" },
  // A REFUSAL IS QUIET AND SAYS "LOAN", NOT "LOANED". The tense is the whole
  // of the tag: nothing was ever loaned, so the word that would imply the
  // object moved is the one word this line must not use — and it takes
  // `loan.withdrawn`'s quiet tone because both are a proposal ending with the
  // picture where it started.
  "loan.declined": { label: "Loan", tone: "quiet" },
  "loan.dispatched": { label: "Loaned", tone: "neutral" },
  "loan.received": { label: "Loaned", tone: "neutral" },
  "loan.ended": { label: "Loaned", tone: "quiet" },
  // "Sold" AT LAST, and only here. The tag `consignment.ended` refuses above is
  // the tag this verb is entitled to: there is an `ondo.transfers` row behind
  // it, and the claim/fact distinction the other three protect is exactly what
  // makes this one honest.
  "work.sold": { label: "Sold", tone: "neutral" },
  // "Ownership", the same word `TITLE_TAG` gives the two history kinds that
  // move title — because this is the moment they were always describing.
  "work.title_transferred": { label: "Ownership", tone: "neutral" },
  // Neither "Sold" nor "Ownership": the sale was unsaid, so a tag naming either
  // would leave the strongest word in the vocabulary on a row that retracts it.
  "work.sale_ended": { label: "Sale ended", tone: "quiet" },
  // MONEY, and a tone of its own would be the fourth kind of fact this pill
  // has to name. `neutral`: a covenant falling due is not a finding, not a
  // dispute and not a grant — it is arithmetic somebody agreed to years ago
  // arriving on time.
  "settlement.due": { label: "Settlement", tone: "neutral" },
  "settlement.settled": { label: "Settlement", tone: "quiet" },
  // The one that IS contested, and the only settlement line that is. An artist
  // saying they were not paid what a covenant owed them is the same shape as a
  // disputed provenance entry, and wears the same tone.
  "settlement.answered": { label: "Settlement", tone: "neutral" },
  "provenance.added": { label: "History", tone: "quiet" },
  "provenance.verification_requested": { label: "Requested", tone: "pending" },
  "provenance.verified": { label: "Verified", tone: "verified" },
  "provenance.disputed": { label: "Disputed", tone: "contested" },
  "provenance.verification_declined": { label: "Declined", tone: "quiet" },
  "provenance.verification_referred": { label: "Passed on", tone: "answer" },
  /**
   * OND-205. "Not delivered", and the tone is the decision rather than the word.
   *
   * `neutral` AND NOT `contested`. Every other pill in this map names a fact
   * about a RECORD or a position somebody took on one; this one names ONDO
   * failing to post a letter. A red pill — the one `provenance.disputed` wears —
   * would put the mark of a dispute on a row where nobody disagrees with
   * anything, in a column a reader scans rather than reads. It is not `quiet`
   * either: the three quiet pills are things that ENDED, and nothing ended here.
   */
  "digest.missed": { label: "Not delivered", tone: "neutral" },
};

/** The override for the two history kinds that actually move title. */
export const TITLE_TAG = { label: "Ownership", tone: "neutral" } as const;

/** The table's column headings, in order. */
export const COLUMNS = ["User", "Action", "Type", "When"] as const;

export const ACTIVITY_COPY = {
  loading: "Reading your activity…",
  /**
   * The banner. Says what the list is, and what it deliberately is not.
   *
   * THE "APPEND-ONLY" CLAIM CAME OFF HERE — OND-106. It used to end "The trail
   * is append-only and cannot be edited here", which is true of the rows in
   * Postgres and NOT of this list. This screen reads Stream, and
   * `ActivityFeedService.record()` swallows every error so that an outage
   * cannot fail a write — CLAUDE.md rule 4 — so a line can be silently missing
   * from here while the row behind it stands. Calling that an append-only
   * trail sells a projection as an audit record, which is the distinction
   * OND-106 exists to keep: prevention, deterrence and an audit record are
   * three different things, and so are a feed and a trail.
   *
   * What replaces it claims only what this screen can keep: nothing here is
   * editable, every line has a durable row under it, and the list may be short
   * of one. The append-only guarantee belongs to the tables.
   */
  banner:
    "Everything recorded against the works and collections you can read, from both sides — a verification you asked for and one you were asked for are the same line, read from opposite ends. Nothing here can be edited from this screen, and every line stands on a durable row — though the list is a feed built from those rows, so a line can be missing from it while the row behind it stands.",
  notInbox:
    "This is not your inbox. What the registry needs from you is in Notifications.",
  order: "Most recent first",
  /** The em-dash cell for a row whose actor outlived whatever recorded it. */
  noActor: "—",
  self: "You",
  /** Shown when a feed came back exactly full — the page is the ceiling. */
  capped:
    "Only the most recent entries are shown. There is no older page to open yet.",
} as const;

/** How many rows one read returns. Mirrors PAGE_SIZE in the API's activity.service.ts. */
export const ACTIVITY_PAGE = 50;

export const EMPTY_COPY = {
  never: {
    lead: "Nothing has been recorded yet.",
    detail:
      "Registering a work, sharing a collection or adding to an artwork's history will each file a line here.",
  },
  filtered: {
    lead: "Nothing in this group.",
    detail: "Clear a filter above to widen the list.",
  },
} as const;

/**
 * What this log still does not carry.
 *
 * Kept, rather than deleted with the placeholder copy it replaces. That copy
 * promised the whole append-only ledger — grants, placements, decisions — and
 * what ships is the ACTIVITY feed, which is narrower. Saying so is the
 * difference between a known gap and a silent over-claim.
 */
export const NOT_HERE = {
  title: "Not recorded here",
  points: [
    // AMENDED BY OND-68, not deleted. This point used to read "Opening a record
    // files nothing, so there is no view history to show", which was true when
    // it was written and is now false. It still belongs on THIS tab, because a
    // view is still not an activity — it has moved rather than gone.
    "Reads. Opening a record is recorded, on the Access trail — not here, because this list is what was done to a record rather than what was seen.",
    "Access decisions. A grant ending ends a row in Postgres, and that ledger is not readable through this screen.",
    "Identity checks. They happen outside the registry, and ONDO files no activity for them.",
  ],
} as const;


/**
 * The two tabs this screen has, and the two different things they read.
 *
 * NOT A COSMETIC SPLIT. The Activity tab reads `GET /v1/activity?scope=me`,
 * which is Stream — a projection that is ALLOWED to lose a line, because
 * `ActivityFeedService.record()` swallows every error so an outage cannot fail
 * a write. The Access tab reads `GET /v1/access/trail`, which is Postgres, is
 * append-only, cannot be deleted by anybody including the service role, and is
 * written inside the request that served the thing it records.
 *
 * That is why they are two tabs and not one merged list. CLAUDE.md: "any screen
 * that reads a feed to answer 'what is true', rather than 'what happened', is a
 * bug." Interleaving the two would make every line as weak as the weakest, and
 * the audit's whole value is that it is not.
 *
 * `aria-current` rather than `role="tablist"`, matching `Verifications.tsx`
 * rather than `WorkTabs.tsx`: two panels with genuinely different content and
 * no roving-tabindex strip to manage.
 */
export const ACTIVITY_TABS = [
  {
    id: "activity",
    label: "Activity",
    /** What the panel is, printed under the tab strip. */
    lead: "What has been recorded against the works and collections you can read.",
  },
  {
    id: "access",
    label: "Access trail",
    lead: "Who has opened a record, read the collector list, or taken a file out of it.",
  },
] as const satisfies readonly { id: string; label: string; lead: string }[];

export type ActivityTabId = (typeof ACTIVITY_TABS)[number]["id"];

export const DEFAULT_ACTIVITY_TAB: ActivityTabId = "activity";

/** The access table's column headings, in order. */
export const ACCESS_COLUMNS = ["User", "Acting for", "Looked at", "When"] as const;

/**
 * What each subject is called on screen.
 *
 * Keyed by `AccessSubject`, so a value added to the Postgres enum and to
 * `ACCESS_SUBJECTS` fails to compile here rather than reaching a row unlabelled
 * — the arrangement `ACTIVITY_TAGS` already has for activity types.
 *
 * `tone` is the pill. A view is `neutral`, because looking at a record you are
 * entitled to open is not an event anybody did anything wrong by; an export is
 * `warn`, because a file has left the registry and cannot be recalled.
 */
export const ACCESS_SUBJECT_TAGS: Record<
  AccessSubject,
  { label: string; glyph: string; tone: TagTone }
> = {
  collector_list: { label: "Collector list", glyph: "◌", tone: "neutral" },
  // `answer` and NOT `contested`: a file leaving the registry is the one line
  // here with a consequence nobody can recall, so it should stand out — but an
  // authorised export is not a dispute, and a red pill against a colleague's
  // name would make an audit trail read as an accusation.
  collector_list_export: { label: "Export", glyph: "↧", tone: "answer" },
  work_record: { label: "Work record", glyph: "◌", tone: "neutral" },
};

export const ACCESS_COPY = {
  loading: "Reading the access trail…",
  /**
   * The banner, and every clause of it is load-bearing.
   *
   * It says what a row MEANS, because the product's claim about this screen was
   * stronger than anything a server can know: "any time anybody even accesses
   * this or looks at it, all of that is recorded". Recorded, yes. What the
   * person then did with what they saw is not knowable here, and the second
   * sentence refuses to imply it — the same refusal OND-67 wrote against
   * `delivered_at`.
   */
  banner:
    "Every record opened, collector list read and file exported, with who was acting for whom. Append-only: no one can edit or delete a line here, including ONDO.",
  limit:
    "A line says ONDO served something to a signed-in session. It is not evidence of what was then kept, copied or passed on, and nothing in the registry can establish that.",
  order: "Most recent first",
  /**
   * The "Looked at" cell for a work this reader cannot open.
   *
   * NOT AN ERROR AND NOT A MISSING VALUE. A trail row names the work only when
   * the reader could open that work anyway, or an audit log would become a
   * lookup — see `20261005096000` decision 10. The row still says who looked
   * and when, which is what it is for.
   */
  unnamedWork: "A record you cannot open",
  self: "You",
  /** The "Acting for" cell for a read somebody took as themselves. */
  asThemselves: "—",
  capped:
    "Only the most recent entries are shown. There is no older page to open yet.",
} as const;

/** How many rows one read returns. Mirrors TRAIL_PAGE in the API's access.service.ts. */
export const ACCESS_PAGE = 200;

export const ACCESS_EMPTY = {
  /**
   * Nothing at all, for anybody — which for an administrator means the firm has
   * opened nothing since the trail began.
   */
  never: {
    lead: "Nothing has been opened yet.",
    detail:
      "Opening a work record, reading the collector list or exporting it will each file a line here.",
  },
  /**
   * The honest empty state for an ordinary seat, and the reason it is not the
   * one above. A viewer sees their OWN rows; the firm's belong to whoever
   * administers it. An empty list here means "you have opened nothing", and
   * must never be read as "nobody has opened anything".
   */
  mineOnly: {
    lead: "You have not opened anything yet.",
    detail:
      "This trail shows your own reads. The firm's whole trail is visible to its administrators.",
  },
} as const;
